export class ArrayUtils {
  /**
   * Partitions an array into two separate arrays based on a given predicate.
   *
   * @param {T[]} list - The array to be partitioned.
   * @param {(item: T) => boolean} predicate - The predicate function used to
   * determine the partition.
   * @returns {[T[], T[]]} - An array containing two arrays: the first array
   * contains elements that satisfy the predicate, and the second array contains
   * elements that do not satisfy the predicate.
   */
  static partition<T>(list: T[], predicate: (item: T) => boolean): [T[], T[]] {
    return list.reduce(
      ([t, f]: [T[], T[]], item) => {
        if (predicate(item)) {
          t.push(item);
        } else {
          f.push(item);
        }

        return [t, f];
      },
      [[], []]
    );
  }

  /**
   * Splits a list into sub-lists stored in an object, based on the result of
   * calling a key-returning function on each element, and grouping the results
   * according to keys generated
   *
   * @param list - The array of objects of type T to be grouped.
   * @param predicate - A function `(T) => string` which returns a string to
   * group by based on items in the list
   * @returns An object where the keys are the values of the predicate function,
   * and the values are arrays of objects which map to those keys
   */
  static groupBy<T, U extends string | number = string>(list: T[], predicate: (i: T) => U): { [k in U]: T[] } {
    return list.reduce(
      (acc, item) => {
        const k = predicate(item);
        if (!acc[k]) {
          acc[k] = [];
        }

        acc[k].push(item);

        return acc;
      },
      {} as Record<U, T[]>
    );
  }

  /**
   * Given an array and a function that generates a key from items in that array,
   * turns a list of objects into an object indexing the objects by the generated
   * key. Note that if multiple objects generate the same value for the indexing
   * key only the first value will be included in the generated object
   *
   * @param list - The array of objects to be grouped.
   * @param predicate - A function [(T) => string] which returns a string to group by based on items in the list
   * @returns An object where the keys are the values of the predicate function, and the values are the first object
   * from the list which maps to the key generated by the predicate
   */
  static indexBy<T, U extends string | number = string>(list: T[], predicate: (i: T) => U): { [id in U]: T } {
    return list.reduce(
      (acc, item) => {
        const k = predicate(item);
        if (!acc[k]) {
          acc[k] = item;
        }
        return acc;
      },
      {} as Record<U, T>
    );
  }

  /**
   * @function collectBy
   * @param list List of items to collect
   * @param predicate A function which turns an item from the list into a key
   * @returns An array of arrays where each sub-array is a collection of all
   * items from `list` (in order) which return the same key under the provided
   * predicate function
   *
   * @example
   * collectBy([
   *  { grade: "A", name: "Alice" }
   *  { grade: "C", name: "Charlie" }
   *  { grade: "B", name: "Bob" }
   *  { grade: "C", name: "Cameron" }
   *  { grade: "B", name: "Brent" }
   * ]) => [
   *  [ { grade: "A", name: "Alice" } ]
   *  [ { grade: "C", "Charlie" }, { grade: "C", name: "Cameron" } ]
   *  [ { grade: "B", "Bob" }, { grade: "B", name: "Brent" } ]
   * ]
   */
  static collectBy<T, U extends string | number = string>(list: T[], predicate: (i: T) => U): Array<T[]> {
    const keyOrder: U[] = [];
    const grouped = {} as Record<U, T[]>;

    for (const item of list) {
      const key = predicate(item);
      if (!(key in grouped)) {
        keyOrder.push(key);
        grouped[key] = [];
      }
      grouped[key].push(item);
    }

    return keyOrder.map((k) => grouped[k]);
  }

  static arraysEqualByKeys<T extends object>(a: T[], b: T[], keys: (keyof T)[]): boolean {
    if (a.length !== b.length) return false;

    const normalize = (arr: T[]) =>
      arr
        .map((obj) =>
          JSON.stringify(
            keys.reduce((res, key) => {
              res[key] = obj[key];
              return res;
            }, {} as Partial<T>)
          )
        )
        .sort();

    const aNormalized = normalize(a);
    const bNormalized = normalize(b);

    return aNormalized.every((val, i) => val === bNormalized[i]);
  }

  static uniqueBy<T>(list: T[], predicate: (i: T) => string): T[] {
    const s = new Set();

    return list
      .map((i) => {
        const k = predicate(i);
        if (!s.has(k)) {
          s.add(k);
          return i;
        }

        return null;
      })
      .filter((i) => i !== null);
  }
}
